import { useState, useEffect, useCallback } from 'react';
import { GeolocationService, type GeolocationError } from '@/services/geolocation';
import type { UserLocation, Location } from '@/types';

interface UseGeolocationOptions {
  enableHighAccuracy?: boolean;
  timeout?: number;
  maximumAge?: number;
  watch?: boolean;
}

interface UseGeolocationReturn {
  location: UserLocation | null;
  isLoading: boolean;
  error: GeolocationError | null;
  isSupported: boolean;
  getCurrentLocation: () => Promise<void>;
  clearError: () => void;
}

/**
 * Hook for handling user geolocation
 */
export function useGeolocation(options: UseGeolocationOptions = {}): UseGeolocationReturn {
  const [location, setLocation] = useState<UserLocation | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<GeolocationError | null>(null);
  
  const isSupported = GeolocationService.isAvailable();

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const getCurrentLocation = useCallback(async () => {
    if (!isSupported) {
      setError({
        code: 0,
        message: 'Geolocation is not supported by this browser',
      });
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const userLocation = await GeolocationService.getCurrentLocation();
      setLocation(userLocation);
    } catch (err) {
      setError(err as GeolocationError);
    } finally {
      setIsLoading(false);
    }
  }, [isSupported]);

  // Auto-get location on mount if watch is enabled
  useEffect(() => {
    if (options.watch && isSupported) {
      getCurrentLocation();
    }
  }, [getCurrentLocation, options.watch, isSupported]);

  // Set up location watching if enabled
  useEffect(() => {
    if (!options.watch || !isSupported) return;

    let watchId: number;

    const handleLocationUpdate = (newLocation: UserLocation) => {
      setLocation(newLocation);
      setIsLoading(false);
      setError(null);
    };

    const handleError = (err: GeolocationError) => {
      setError(err);
      setIsLoading(false);
    };

    // eslint-disable-next-line prefer-const
    watchId = GeolocationService.watchLocation(handleLocationUpdate, handleError);

    return () => {
      if (watchId) {
        GeolocationService.stopWatching();
      }
    };
  }, [options.watch, isSupported]);

  return {
    location,
    isLoading,
    error,
    isSupported,
    getCurrentLocation,
    clearError,
  };
}

/**
 * Hook for managing a selected location (either from geolocation or manual selection)
 */
interface UseLocationSelectionReturn {
  selectedLocation: Location | null;
  userLocation: UserLocation | null;
  isUsingUserLocation: boolean;
  isLoading: boolean;
  error: GeolocationError | null;
  hasLocation: boolean;
  locationDisplayName: string;
  setSelectedLocation: (location: Location, displayName?: string) => void;
  useUserLocation: () => void;
  getCurrentLocation: () => Promise<void>;
  clearError: () => void;
  reset: () => void;
}

export function useLocationSelection(): UseLocationSelectionReturn {
  const [selectedLocation, setSelectedLocationState] = useState<Location | null>(null);
  const [isUsingUserLocation, setIsUsingUserLocation] = useState(false); // Changed default to false
  const [locationDisplayName, setLocationDisplayName] = useState<string>('');
  
  const {
    location: userLocation,
    isLoading,
    error,
    getCurrentLocation,
    clearError,
  } = useGeolocation({ watch: false });

  const setSelectedLocation = useCallback((location: Location, displayName?: string) => {
    setSelectedLocationState(location);
    setIsUsingUserLocation(false);
    setLocationDisplayName(displayName || 'Selected Location');
  }, []);

  const useUserLocation = useCallback(() => {
    setIsUsingUserLocation(true);
    setSelectedLocationState(null);
    setLocationDisplayName('Current Location');
    getCurrentLocation();
  }, [getCurrentLocation]);

  const reset = useCallback(() => {
    setIsUsingUserLocation(false);
    setSelectedLocationState(null);
    setLocationDisplayName('');
    clearError();
  }, [clearError]);

  // Determine the effective selected location
  const effectiveLocation = isUsingUserLocation 
    ? userLocation?.coordinates || null
    : selectedLocation;

  const hasLocation = effectiveLocation !== null;

  return {
    selectedLocation: effectiveLocation,
    userLocation,
    isUsingUserLocation,
    isLoading,
    error,
    hasLocation,
    locationDisplayName,
    setSelectedLocation,
    useUserLocation,
    getCurrentLocation,
    clearError,
    reset,
  };
}

/**
 * Hook for calculating distances to multiple locations
 */
export function useDistanceCalculation(
  fromLocation: Location | null,
  toLocations: Location[]
) {
  const [distances, setDistances] = useState<number[]>([]);

  useEffect(() => {
    if (!fromLocation || toLocations.length === 0) {
      setDistances([]);
      return;
    }

    const calculatedDistances = toLocations.map(location =>
      GeolocationService.calculateDistance(fromLocation, location)
    );

    setDistances(calculatedDistances);
  }, [fromLocation, toLocations]);

  return distances;
}

/**
 * Hook for formatting and displaying location information
 */
export function useLocationDisplay(location: Location | null) {
  const [address, setAddress] = useState<string>('');

  useEffect(() => {
    if (!location) {
      setAddress('');
      return;
    }

    // For now, just show coordinates
    // In a real app, you'd reverse geocode to get an address
    setAddress(GeolocationService.formatCoordinates(location));
  }, [location]);

  return {
    coordinates: location ? GeolocationService.formatCoordinates(location) : '',
    address,
  };
}